import getProductCategoriesApi from '@/api/get/company/get-product-categories';
import { getSupplierProductsByProductCategoryApiLazy } from '@/api/get/company/sales-flow/get-supplier-products-by-product-category';
import SubmitCard from '@/components/card/submit-card-component';
import { Row } from '@/components/grid/grid-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { Col } from '@/components/table/components/grid/grid-component';
import { useLazyQuery, useMutation, useQuery } from '@/hooks/use-api';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { MdOutlineDragIndicator } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useEffect, useRef, useState } from 'react';
import updateCompanyProductOrderApi from '@/api/patch/company/company-product/update-company-product-order';
import OptionSVG from '@/assets/svg/icon_options.svg';
import Alert from '@/components/alert/alert-component';
import Styled from './reorder-style';

/**
 * Reorder product portal component.
 * @param {object} props The component props.
 * @param {object} props.children The children to render.
 * @returns {null|JSX.Element} The rendered ReorderProductPortal component.
 */
const ReorderProductPortal = ({ children }) => {
  const reorderProductPortal = document.getElementById('reorderProductPortal');
  if (reorderProductPortal) {
    return createPortal(children, reorderProductPortal);
  }
  return null;
};

ReorderProductPortal.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Reorder products component.
 * @param {object} props The component props.
 * @param {Function} props.onClose The function to close the modal.
 * @returns {JSX.Element} The rendered Reorder Products page component.
 */
const ReorderProducts = ({ onClose }) => {
  const { id } = useParams();
  const methods = useForm();
  const [products, setProducts] = useState([]);
  const containerRef = useRef(null);
  const { data: productCategories, loading: productCategoriesLoading } =
    useQuery(getProductCategoriesApi, id);

  const {
    data: productData,
    loading: productsLoading,
    refetch: refetchProducts,
  } = useLazyQuery(getSupplierProductsByProductCategoryApiLazy(id));

  const selectedProductCategoryId = useWatch({
    name: 'product_category_id',
    control: methods.control,
  });

  const [
    updateCompanyProductOrder,
    { loading: updateCompanyProductOrderLoading },
  ] = useMutation(updateCompanyProductOrderApi);

  // Update products state when productData changes
  useEffect(() => {
    if (productData) {
      setProducts(productData);
    }
  }, [productData]);

  // Set up drag and drop for individual items
  const setupDraggable = (element, index) => {
    if (!element) return;

    const cleanup = draggable({
      element,
      getInitialData: () => ({ index: index.toString() }),
    });

    return cleanup;
  };

  // Set up drop target for individual items
  const setupDropTarget = (element, index) => {
    if (!element) return;

    const cleanup = dropTargetForElements({
      element,
      getData: () => ({ index: index.toString() }),
      onDrop: ({ source }) => {
        const sourceIndex = parseInt(source.data.index, 10);
        const targetIndex = index;

        if (sourceIndex !== targetIndex) {
          const newProducts = [...products];
          const [movedItem] = newProducts.splice(sourceIndex, 1);
          newProducts.splice(targetIndex, 0, movedItem);
          setProducts(newProducts);
        }
      },
    });

    return cleanup;
  };

  return (
    <FormProvider {...methods}>
      <Styled.ReorderProductsContainer>
        {selectedProductCategoryId && (
          <Styled.ProductCategoryContainer>
            <Row ref={containerRef} className="draggable-container">
              {!productsLoading &&
                products?.map((product, idx) => (
                  <Col
                    className="draggable-item"
                    key={`${product.name}_${idx + 1}`}
                    sm={6}
                    md={4}
                    lg={4}
                    xl={3}
                    xxl={3}
                    $minHeight="240px"
                    $marginBottom="30px"
                  >
                    <Styled.ProductFilterGroupItem
                      ref={(element) => {
                        if (element) {
                          const draggableCleanup = setupDraggable(element, idx);
                          const dropTargetCleanup = setupDropTarget(
                            element,
                            idx,
                          );

                          // Return a cleanup function that cleans up both
                          return () => {
                            draggableCleanup?.();
                            dropTargetCleanup?.();
                          };
                        }
                      }}
                    >
                      <Styled.DragIndicator>
                        <MdOutlineDragIndicator />
                      </Styled.DragIndicator>
                      <Styled.ImageHolder>
                        {product.thumbnail ? (
                          <Styled.ProductFilterGroupItemImage
                            src={product.thumbnail}
                            alt={product.name}
                          />
                        ) : (
                          <Styled.NoThumbnail />
                        )}
                      </Styled.ImageHolder>
                      <Styled.ProductFilterGroupItemDescription>
                        {product.main_category}
                      </Styled.ProductFilterGroupItemDescription>
                      <Styled.ProductFilterGroupItemTitle>
                        <Styled.TitleItems>{product.name}</Styled.TitleItems>
                      </Styled.ProductFilterGroupItemTitle>
                    </Styled.ProductFilterGroupItem>
                  </Col>
                ))}
            </Row>
          </Styled.ProductCategoryContainer>
        )}
        {!selectedProductCategoryId && (
          <Styled.NoProductCategory>
            <Styled.NoProductCategoryImg src={OptionSVG} alt="No option img" />
            <Styled.NoProductCategoryHeaderText>
              Please select a product category
            </Styled.NoProductCategoryHeaderText>
            <Styled.NoProductCategoryText>
              To reorder your products, first choose a category. Then, drag and
              drop the products within that category to set your preferred
              order.
            </Styled.NoProductCategoryText>
          </Styled.NoProductCategory>
        )}
        <SubmitCard
          padding="0"
          primaryButtonTitle="Update Order"
          primaryButtonType="submit"
          primaryButtonDisabled={false}
          primaryButtonWidth="152px"
          submitClick={() => {
            const newOrder = products.map((product, index) => ({
              id: product.id,
              order: index,
            }));
            updateCompanyProductOrder(id, {
              products: newOrder,
            })
              .then(() => {
                onClose();
                Alert({
                  icon: 'success',
                  text: 'Product order updated successfully',
                });
              })
              .catch(() => {
                Alert({
                  icon: 'error',
                  text: 'Product order failed to update',
                });
              });
          }}
          fixToBottom
          submitClickSecondary={() => {
            onClose();
          }}
          isLoading={updateCompanyProductOrderLoading || productsLoading}
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          secondaryButtonWidth="112px"
          disabled={false}
          secondaryButtonTitle="Cancel"
        />
        <ReorderProductPortal>
          <SelectSearch
            name="product_category_id"
            label="Please select a product category"
            customStyle={{
              width: '220px',
            }}
            options={
              productCategories?.map((c) => ({
                label: c.name,
                value: c.id,
              })) || []
            }
            isLoading={productCategoriesLoading}
            renderError={false}
            onChange={(opt) => {
              const value = opt?.value;
              if (value) {
                refetchProducts(value);
              }
            }}
          />
        </ReorderProductPortal>
      </Styled.ReorderProductsContainer>
    </FormProvider>
  );
};

ReorderProducts.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ReorderProducts;
